import { cn } from "@d12labs/utils";
import * as React from "react";
import * as RechartsPrimitive from "recharts";

/**
 * Chart primitive — a thin, theme-aware wrapper over Recharts, shadcn-compatible.
 *
 * Recharts gives you the geometry (lines, bars, radars); this gives you the
 * chassis design language for free: series colors resolve from a `ChartConfig`
 * to CSS variables (`--color-<key>`), with `--chart-1..5` as defaults so a chart
 * with no explicit colors still matches the palette. Tooltip and legend render
 * with the app's popover/foreground tokens instead of Recharts' stock styling.
 *
 *   const config = {
 *     clarity: { label: "Clarity", color: "var(--color-chart-1)" },
 *     confidence: { label: "Confidence", color: "var(--color-chart-2)" },
 *   } satisfies ChartConfig;
 *
 *   <ChartContainer config={config}>
 *     <RadarChart data={data}>
 *       <ChartTooltip content={<ChartTooltipContent />} />
 *       <Radar dataKey="clarity" fill="var(--color-clarity)" />
 *     </RadarChart>
 *   </ChartContainer>
 *
 * Drop-in compatible with shadcn/ui chart docs and examples.
 */

const THEMES = { light: "", dark: ".dark" } as const;

export type ChartConfig = {
	[key: string]: {
		label?: React.ReactNode;
		icon?: React.ComponentType;
	} & (
		| { color?: string; theme?: never }
		| { color?: never; theme: Record<keyof typeof THEMES, string> }
	);
};

type ChartContextProps = { config: ChartConfig };

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart(): ChartContextProps {
	const context = React.useContext(ChartContext);
	if (!context) {
		throw new Error("useChart must be used within a <ChartContainer />");
	}
	return context;
}

function ChartContainer({
	id,
	className,
	children,
	config,
	initialDimension,
	...props
}: React.ComponentProps<"div"> & {
	config: ChartConfig;
	children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"];
	/** Size before the first measure. Set it for a fixed-height chart to skip Recharts' -1×-1 warning. */
	initialDimension?: React.ComponentProps<
		typeof RechartsPrimitive.ResponsiveContainer
	>["initialDimension"];
}) {
	const uniqueId = React.useId();
	const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

	return (
		<ChartContext.Provider value={{ config }}>
			<div
				data-slot="chart"
				data-chart={chartId}
				className={cn(
					"flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
					className,
				)}
				{...props}
			>
				<ChartStyle id={chartId} config={config} />
				<RechartsPrimitive.ResponsiveContainer initialDimension={initialDimension}>
					{children}
				</RechartsPrimitive.ResponsiveContainer>
			</div>
		</ChartContext.Provider>
	);
}

function ChartStyle({ id, config }: { id: string; config: ChartConfig }) {
	const colorConfig = Object.entries(config).filter(([, c]) => c.theme || c.color);
	if (!colorConfig.length) return null;

	const css = Object.entries(THEMES)
		.map(([theme, prefix]) => {
			const vars = colorConfig
				.map(([key, itemConfig]) => {
					const color = itemConfig.theme?.[theme as keyof typeof THEMES] || itemConfig.color;
					return color ? `  --color-${key}: ${color};` : null;
				})
				.filter(Boolean)
				.join("\n");
			return `${prefix} [data-chart=${id}] {\n${vars}\n}`;
		})
		.join("\n");

	// biome-ignore lint/security/noDangerouslySetInnerHtml: emitting a scoped <style> block keyed on a generated chart id, no user input
	return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

const ChartTooltip = RechartsPrimitive.Tooltip;

type TooltipItem = {
	name?: string;
	value?: number | string;
	dataKey?: string | number;
	color?: string;
	fill?: string;
	payload?: Record<string, unknown>;
};

function configKey(item: TooltipItem, fallback: "name" | "dataKey"): string {
	return String(item[fallback] ?? "");
}

function ChartTooltipContent({
	active,
	payload,
	label,
	className,
	hideLabel = false,
	hideIndicator = false,
	indicator = "dot",
	labelFormatter,
}: {
	active?: boolean;
	payload?: TooltipItem[];
	label?: React.ReactNode;
	className?: string;
	hideLabel?: boolean;
	hideIndicator?: boolean;
	indicator?: "line" | "dot" | "dashed";
	labelFormatter?: (label: React.ReactNode) => React.ReactNode;
}) {
	const { config } = useChart();

	if (!active || !payload?.length) return null;

	const renderedLabel = hideLabel || !label ? null : labelFormatter ? labelFormatter(label) : label;

	return (
		<div
			className={cn(
				"border-border/50 bg-popover text-popover-foreground grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl",
				className,
			)}
		>
			{renderedLabel ? <div className="font-medium">{renderedLabel}</div> : null}
			<div className="grid gap-1.5">
				{payload.map((item, index) => {
					const key = configKey(item, "dataKey") || configKey(item, "name") || `item-${index}`;
					const itemConfig = config[key];
					const indicatorColor = item.color || item.fill;
					return (
						<div
							key={key}
							className="flex w-full flex-wrap items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5"
						>
							{!hideIndicator ? (
								<div
									className={cn("shrink-0 rounded-[2px]", {
										"h-2.5 w-2.5": indicator === "dot",
										"w-1": indicator === "line",
										"w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
									})}
									style={{
										backgroundColor: indicator !== "dashed" ? indicatorColor : undefined,
										borderColor: indicatorColor,
									}}
								/>
							) : null}
							<div className="flex flex-1 justify-between leading-none">
								<span className="text-muted-foreground">{itemConfig?.label ?? item.name}</span>
								{item.value != null ? (
									<span className="text-foreground font-mono font-medium tabular-nums">
										{typeof item.value === "number" ? item.value.toLocaleString() : item.value}
									</span>
								) : null}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

const ChartLegend = RechartsPrimitive.Legend;

type LegendItem = { value?: string; dataKey?: string | number; color?: string };

function ChartLegendContent({
	className,
	payload,
	hideIcon = false,
	verticalAlign = "bottom",
}: {
	className?: string;
	payload?: LegendItem[];
	hideIcon?: boolean;
	verticalAlign?: "top" | "bottom";
}) {
	const { config } = useChart();
	if (!payload?.length) return null;

	return (
		<div
			className={cn(
				"flex items-center justify-center gap-4",
				verticalAlign === "top" ? "pb-3" : "pt-3",
				className,
			)}
		>
			{payload.map((item, index) => {
				const key = String(item.dataKey ?? item.value ?? `item-${index}`);
				const itemConfig = config[key];
				return (
					<div key={key} className="flex items-center gap-1.5 text-muted-foreground">
						{!hideIcon ? (
							<div
								className="h-2 w-2 shrink-0 rounded-[2px]"
								style={{ backgroundColor: item.color }}
							/>
						) : null}
						{itemConfig?.label ?? item.value}
					</div>
				);
			})}
		</div>
	);
}

export {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartStyle,
	ChartTooltip,
	ChartTooltipContent,
	useChart,
};
